// Views/About.jsx
import React from "react";
import { motion } from "framer-motion";
import {
  FaRocket,
  FaLightbulb,
  FaUsers,
  FaHeart,
  FaAward,
  FaGlobeAmericas,
  FaHandshake,
  FaChartLine,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const About = () => {
  const navigate = useNavigate();
  // Full-Screen Hero Section with Black & White Image
  const HeroSection = () => (
    <section className="relative h-screen w-full overflow-hidden">
      {/* Full Screen Black & White Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80"
          alt="Our Team"
          className="h-full w-full object-cover grayscale"
        />
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80"></div>
      </div>

      {/* Animated particles/dots effect */}
      <div className="absolute inset-0 z-10 pointer-events-none">
        <motion.div
          animate={{
            opacity: [0.1, 0.3, 0.1],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-cyan-500/20 blur-[100px]"
        />
        <motion.div
          animate={{
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-purple-500/20 blur-[100px]"
        />
      </div>

      {/* Hero Content - Centered */}
      <div className="relative z-20 flex h-full items-center justify-center px-4">
        <div className="max-w-5xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="mb-4 text-sm font-semibold uppercase tracking-widest text-cyan-400"
            >
              About Nexonetics
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="mb-6 text-5xl font-extrabold tracking-tighter text-white md:text-7xl lg:text-8xl drop-shadow-2xl"
            >
              Innovating the
              <br />
              <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                Digital Future
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="mx-auto mb-10 max-w-3xl text-lg font-light leading-relaxed text-gray-200 md:text-xl"
            >
              We are a team of passionate technologists, designers, and
              strategists committed to transforming businesses through
              innovative digital solutions.
            </motion.p>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30">
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex flex-col items-center gap-2 text-white/70"
        >
          <span className="text-xs uppercase tracking-widest">Scroll</span>
          <div className="h-12 w-[2px] bg-gradient-to-b from-white to-transparent"></div>
        </motion.div>
      </div>
    </section>
  );

  // Our Story Section
  const StorySection = () => (
    <section className="bg-white py-24 px-4">
      <div className="mx-auto max-w-7xl">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6 md:text-5xl">
              Our Story
            </h2>
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p>
                Nexonetics began with a simple mission: to bridge the gap
                between innovative technology and business success. What started
                as a small team of developers has grown into a full-service
                digital solutions company.
              </p>
              <p>
                Our journey has been marked by continuous learning, adaptation,
                and a relentless pursuit of excellence. We've evolved from
                building simple websites to creating complex enterprise
                solutions, AI-powered applications, and cutting-edge mobile
                experiences.
              </p>
              <p>
                Today, we're proud to serve clients across the globe, helping
                them navigate digital transformation and achieve their business
                goals through technology.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <img
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=800"
              alt="Team collaboration"
              className="rounded-2xl shadow-2xl grayscale hover:grayscale-0 transition-all duration-500"
            />
            <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-cyan-500 to-purple-500 rounded-2xl p-8 text-white shadow-xl">
              <p className="text-4xl font-bold">7+</p>
              <p className="text-sm">Years of Excellence</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );

  // Mission & Vision Section
  const MissionVisionSection = () => (
    <section className="bg-[#f8fafc] py-24 px-4">
      <div className="mx-auto max-w-7xl">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Mission */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white/60 backdrop-blur-md rounded-3xl p-8 border border-white/60 shadow-xl"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-3xl text-white">
                <FaRocket />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Our Mission</h2>
            </div>
            <p className="text-gray-600 leading-relaxed text-lg">
              To empower businesses with innovative digital solutions that drive
              growth, enhance efficiency, and create lasting impact. We strive
              to be the catalyst for digital transformation, making cutting-edge
              technology accessible and actionable for organizations of all
              sizes.
            </p>
          </motion.div>

          {/* Vision */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-white/60 backdrop-blur-md rounded-3xl p-8 border border-white/60 shadow-xl"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-3xl text-white">
                <FaLightbulb />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Our Vision</h2>
            </div>
            <p className="text-gray-600 leading-relaxed text-lg">
              To be the global leader in digital innovation, recognized for our
              ability to transform visionary ideas into powerful, tangible
              solutions. We envision a future where technology seamlessly
              enhances every aspect of business and life.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );

  // Core Values Section
  const ValuesSection = () => {
    const values = [
      {
        icon: FaLightbulb,
        title: "Innovation",
        description:
          "Constantly pushing boundaries and exploring new possibilities",
        gradient: "from-yellow-500 to-orange-500",
      },
      {
        icon: FaUsers,
        title: "Collaboration",
        description: "Working together to achieve extraordinary results",
        gradient: "from-blue-500 to-cyan-500",
      },
      {
        icon: FaAward,
        title: "Excellence",
        description: "Delivering exceptional quality in everything we do",
        gradient: "from-purple-500 to-pink-500",
      },
      {
        icon: FaHeart,
        title: "Integrity",
        description:
          "Operating with honesty, transparency, and ethical standards",
        gradient: "from-red-500 to-rose-500",
      },
      {
        icon: FaGlobeAmericas,
        title: "Global Impact",
        description: "Creating solutions that make a difference worldwide",
        gradient: "from-green-500 to-emerald-500",
      },
      {
        icon: FaHandshake,
        title: "Customer Focus",
        description: "Putting client success at the heart of our mission",
        gradient: "from-indigo-500 to-purple-500",
      },
    ];

    return (
      <section className="bg-black py-24 px-4 text-white">
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 md:text-5xl">
              Our Core Values
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              The principles that guide our decisions and shape our culture
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-white/30 transition-all"
              >
                <div
                  className={`inline-flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br ${value.gradient} text-2xl text-white mb-4`}
                >
                  <value.icon />
                </div>
                <h3 className="text-xl font-bold mb-2">{value.title}</h3>
                <p className="text-gray-400">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // CTA Section
  const CTASection = () => (
    <section className="bg-white py-24 px-4">
      <div className="mx-auto max-w-4xl text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-6 md:text-5xl">
            Ready to Start Your Journey?
          </h2>
          <p className="text-lg text-gray-600 mb-10 max-w-2xl mx-auto">
            Let's work together to transform your vision into reality. Join the
            many businesses that trust Nexonetics for their digital success.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.a
              onClick={() => navigate("/portfolio")}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 border-2 border-gray-900 text-gray-900 font-bold rounded-full hover:bg-gray-900 hover:text-white transition-all inline-block"
            >
              View Our Work
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );

  return (
    <div className="bg-white overflow-x-hidden">
      <HeroSection />
      <StorySection />
      <MissionVisionSection />
      <ValuesSection />

      <CTASection />
    </div>
  );
};

export default About;
